import { HttpClient, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { StorageService } from '../services/storage.service';
import { ToastService } from '../services/toast.service';
import { ApiResponse } from '../models/response.model';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { LoginResponse } from '../../features/authentication/services/auth.service';

export const errorInterceptor: HttpInterceptorFn = (req, next) =>
{
  const router = inject(Router);
  const storage = inject(StorageService);
  const toast = inject(ToastService);
  const http = inject(HttpClient);

  return next(req).pipe(
    catchError((error) =>
    {
      const message: string =
        error.error?.message ?? error.statusText ?? 'Something went wrong';

      switch (error.status)
      {
        case 400:
          toast.error(message, 'Bad Request');
          break;
        case 401: {
          const isAuthRequest = req.url.includes('/auth/');
          if (isAuthRequest)
          {
            toast.error(message, 'Unauthorized');
          } else
          {
            const refreshToken = storage.get('refresh_token');
            if (refreshToken)
            {
              return http.post<LoginResponse>(API_ENDPOINTS.auth.refreshToken, { refreshToken }).pipe(
                switchMap((res) =>
                {
                  storage.set('access_token', res.data.accessToken);
                  storage.set('refresh_token', res.data.refreshToken);

                  const retryReq = req.clone({
                    setHeaders: {
                      Authorization: `Bearer ${ res.data.accessToken }`,
                    },
                  });
                  return next(retryReq);
                }),
                catchError(() =>
                {
                  storage.remove('access_token');
                  storage.remove('refresh_token');
                  toast.error('Session expired. Please login again.', 'Unauthorized');
                  router.navigate(['/auth/login']);

                  const apiError: ApiResponse = {
                    message: 'Session expired. Please login again.',
                    data: null,
                    success: false,
                    errors: [],
                  };
                  return throwError(() => apiError);
                }),
              );
            }

            storage.remove('access_token');
            storage.remove('refresh_token');
            toast.error('Session expired. Please login again.', 'Unauthorized');
            router.navigate(['/auth/login']);
          }
          break;
        }
        case 403:
          toast.error('You do not have permission to perform this action.', 'Forbidden');
          break;
        case 404:
          toast.error(message, 'Not Found');
          break;
        case 409:
          toast.error(message, 'Conflict');
          break;
        case 422:
          toast.error(message, 'Validation Error');
          break;
        case 429:
          toast.error('Too many requests. Please try again later.', 'Rate Limited');
          break;
        case 500:
          toast.error('Internal server error. Please try again later.', 'Server Error');
          break;
        case 503:
          toast.error('Service is temporarily unavailable. Please try again later.', 'Unavailable');
          break;
        case 0:
          toast.error('Unable to connect to server. Check your internet connection.', 'Network Error');
          break;
        default:
          toast.error(message);
          break;
      }

      // Normalize the error into the standardized ApiResponse format before re-throwing
      const apiError: ApiResponse = {
        message,
        data: null,
        success: false,
        errors: error.error?.errors ?? [],
      };

      return throwError(() => apiError);
    })
  );
};
